import { capitalize } from "lodash";

import {
  Accession,
  Case,
  CaseAdmin,
  CaseReportSignature,
  Specimen,
} from "pwapi";

import { CaseDetailState } from "reducers/caseDetailReducer";

import { isDermDXCert } from "./labUtils";
import { isNoBillingConstrainedCaseType } from "./rulesEngineUtils";
import { CaseListDirection } from "../reducers/casesReducer";

export const getNextCaseId = (
  currentCaseId?: number,
  isListPending?: boolean,
  idList?: number[]
): number | undefined => {
  if (!currentCaseId || isListPending || !idList) {
    return undefined;
  }

  const caseIndex = idList.indexOf(currentCaseId);
  return caseIndex !== -1 && !(caseIndex >= idList.length - 1)
    ? idList[caseIndex + 1]
    : undefined;
};

export const getPrevCaseId = (
  currentCaseId?: number,
  isListPending?: boolean,
  idList?: number[]
): number | undefined => {
  if (!currentCaseId || isListPending || !idList) {
    return undefined;
  }

  const caseIndex = idList.indexOf(currentCaseId);
  return caseIndex !== -1 && !(caseIndex === 0)
    ? idList[caseIndex - 1]
    : undefined;
};

export const getCaseOrdering = (
  sortDirection: CaseListDirection = CaseListDirection.Asc,
  sortColumn: string
): string => {
  const descending = sortDirection === CaseListDirection.Desc ? "-" : "";
  return [
    {
      accession: `${descending}accession`,
      clinician: `${descending}ordering_doctor`,
      finalized_at: `-status,${descending}finalized_at`,
      ordering_doctor: `${descending}ordering_doctor_name`,
      patient: `${descending}patient__last_name,${descending}patient__first_name`,
      priority: `-priority,-patient__clinic__tat_priority,received_date`,
      specimens__received_date: `${descending}received_date`,
      specimens__collected_date: `${descending}collected_date`,
    }[sortColumn] || `${descending}${sortColumn}`,
  ]
    .filter((a) => a)
    .join(",");
};

export const getSpecimenCollectedDate = (specimens: Specimen[]) => {
  return specimens.find((specimen) => specimen?.collected_date)?.collected_date;
};

const billingTypeLabels = {
  global: "Third-Party Bill",
  client: "Client Bill",
  cash: "Cash Pay",
  none: "No Bill",
};
export const billingTypeEnumToLabel = (shortEnum: Case.billing_type) => {
  return (shortEnum && billingTypeLabels[shortEnum]) || shortEnum;
};

export const isCaseTCOnly = (caseDetail: Case | CaseDetailState) => {
  const { bill_tc, bill_pc } = caseDetail;
  if (bill_tc && !bill_pc) return true;
  return false;
};
export const isCasePCOnly = (caseDetail: Case | CaseDetailState) => {
  const { bill_tc, bill_pc } = caseDetail;
  if (bill_pc && !bill_tc) return true;
  return false;
};
export const isCaseBayshore = (caseDetail: Case | CaseDetailState) => {
  return (
    caseDetail.grossed_by_cert?.name.toLowerCase().includes("bayshore") ||
    caseDetail.accession?.toUpperCase().startsWith("BAY")
  );
};
export const isSignatureDNE = (signature?: CaseReportSignature) => {
  if (!signature) return false;
  return isDermDXCert(signature.clia_certificate);
};
export const isCaseBayshoreDNE = (
  caseDetail: Case | CaseDetailState,
  signature?: CaseReportSignature
) => {
  return isCaseBayshore(caseDetail) && isSignatureDNE(signature);
};

export const workingReasonText = (
  workingReasons: Case["working_reasons"],
  reason: string,
  pendingLabWorkWorkingReason?: boolean
) => {
  if (reason === "pending-slides")
    return `${workingReasons[reason].length} Pending Slides`;
  if (reason === workingReasons[reason] || workingReasons[reason] === true)
    return reason.split("-").map(capitalize).join(" ");
  if (reason === "incomplete-lis-work" && !pendingLabWorkWorkingReason)
    return "Pending Lab Work";
  return workingReasons[reason];
};

export const displayPriority = (priority: string) => {
  const lowercasePriority = priority.toLowerCase();
  return lowercasePriority === "normal" ? "routine" : lowercasePriority;
};

export const getBillingProperties = (value: string | null) => {
  const billingProperties = {
    self_pay: false,
    self_pay_with_attestation: false,
    indigent: false,
  };

  switch (value) {
    case "self_pay":
      billingProperties.self_pay = true;
      break;
    case "self_pay_with_attestation":
      billingProperties.self_pay = true;
      billingProperties.self_pay_with_attestation = true;
      break;
    case "indigent":
      billingProperties.self_pay = true;
      billingProperties.indigent = true;
      break;
  }

  return billingProperties;
};

// Returns true if the billing_type is set and not included in the clinic's billing_types array
export function isBillingTypeUnsupported(
  billingType?: string | null,
  billingTypes?: string[] | null,
  caseType?: string | null
): boolean {
  // If the case type is one of the no billing constrained types, then we don't want to show the unsupported billing type warning. These all get the "No Bill" option regardless of the clinic's billing types.
  if (isNoBillingConstrainedCaseType(caseType)) {
    return false;
  }

  if (Array.isArray(billingTypes) && billingTypes.length === 0) {
    return false;
  }

  return (
    !!billingType &&
    Array.isArray(billingTypes) &&
    !billingTypes.includes(billingType)
  );
}

export const isNonDiagnosticCase = (
  caseType?: Accession.case_type | CaseAdmin.type
) => {
  if (!caseType) return false;

  const nonDiagnosticCaseTypes: Array<Accession.case_type | CaseAdmin.type> = [
    Accession.case_type.CONSULT,
    Accession.case_type.CONTROL,
    Accession.case_type.DEMO,
    Accession.case_type.EDUCATION,
    Accession.case_type.HISTORICAL,
    Accession.case_type.MOHS_QC,
    Accession.case_type.NON_DIAGNOSTIC,
    Accession.case_type.TEST,
    Accession.case_type.TC_PC,
    Accession.case_type.TRIAGE,
    Accession.case_type.VALIDATION,
  ];

  return nonDiagnosticCaseTypes.includes(caseType);
};

export const getDestinationLabsStr = (
  destinationLabs: { id: number; name: string }[]
) => {
  const destinationLabsName = destinationLabs.flatMap((lab) => lab.name);
  if (destinationLabs.length === 2) return destinationLabsName.join(" and ");
  if (destinationLabs.length > 2)
    return `${destinationLabsName.slice(0, -1).join(", ")}, and ${
      destinationLabsName[destinationLabsName.length - 1]
    }`;
};
